interface IUsePaginationOptions<Item> {
  /**
   * If enabled, the `loadItems` function will be called during the `onMounted` lifecycle hook.
   */
  immediate?: boolean;
  /**
   * The default number of items per page.
   * Default value is 10.
   */
  pageSize?: number;
  /**
   * An optional array of preloaded items.
   */
  preloadedItems?: Item[];
  /**
   * If enabled, the function will paginate existing items instead of loading more items.
   */
  paginateExistItems?: boolean;
  /**
   * A required function to load items, it should return a Promise of IPaginatedResponse<Item>.
   * It receives the current page and page size as arguments.
   */
  loadItemsFn: (page: number, pageSize: number) => Promise<IPaginatedResponse<Item>>;
  /**
   * An optional function that is called when items are successfully loaded.
   * It receives an array of loaded items as an argument.
   */
  onItemsLoadedFn?: (items: Item[]) => void;
}

export default function usePagination<Item>(options: IUsePaginationOptions<Item>) {
  const page = ref(0);
  const items = ref<Item[]>([]) as Ref<Item[]>;
  const itemsToPaginate = ref<Item[]>([]) as Ref<Item[]>;
  const error = ref<string | null>(null);
  const loading = ref(false);
  const limit = options.pageSize || 10;
  const canLoadMore = ref(false);

  function loadMore() {
    page.value += 1;
    if (options.paginateExistItems) {
      paginateItems();
      return;
    }
    loadItems();
  }

  function paginateItems() {
    loading.value = true;
    items.value = items.value.concat(itemsToPaginate.value.slice(page.value * limit, limit * (page.value + 1)));
    if (itemsToPaginate.value.length <= items.value.length)
      canLoadMore.value = false;
    loading.value = false;
  }

  function loadItems() {
    loading.value = true;
    error.value = null;

    return options
      .loadItemsFn(page.value, limit)
      .then((response) => {
        itemsToPaginate.value = response.content.entries;
        items.value = options.paginateExistItems ? itemsToPaginate.value.slice(0, limit) : items.value.concat(response.content.entries);
        canLoadMore.value = response.content.nextExists || (items.value.length < response.content.count);
        loading.value = false;

        if (options.onItemsLoadedFn)
          options.onItemsLoadedFn(items.value);
      })
      .catch((err: Error) => {
        error.value = err.message;
        loading.value = false;
      });
  }

  function reset() {
    page.value = 0;
    items.value = [];

    if (Array.isArray(options.preloadedItems))
      items.value = items.value.concat(options.preloadedItems);

    loadItems();
  }

  onMounted(() => options.immediate && reset());

  return { page, reset, loadMore, items, loading, error, canLoadMore };
}
